<template>
  <div class="space-y-4">
    <div>
      <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="theme-preference">Theme</label>
      <select id="theme-preference" v-model="selectedTheme" class="ui-input w-full">
        <optgroup v-for="group in themeGroups" :key="group" :label="group">
          <option v-for="theme in themeOptions.filter(option => option.group === group)" :key="theme.value" :value="theme.value">{{ theme.label }}</option>
        </optgroup>
      </select>
      <p class="mt-1 text-xs text-gray-400">Applied immediately and saved in this browser for this account. Closing Preferences keeps these changes.</p>
    </div>

    <div v-if="isNamedTheme(activeTheme)" class="space-y-2">
      <div class="flex items-baseline justify-between gap-3">
        <label class="text-xs font-bold uppercase text-gray-500" for="theme-hue">Theme hue</label>
        <output class="text-sm text-gray-700" for="theme-hue">{{ hueLabel }}</output>
      </div>
      <input id="theme-hue" :value="activeThemeHue" class="appearance-wash-slider w-full" type="range" min="-20" max="20" step="1" :aria-valuetext="hueLabel" aria-describedby="theme-hue-help" @input="setThemeHue(numberValue($event), account)" />
      <div class="flex items-center justify-between gap-3">
        <p id="theme-hue-help" class="text-xs text-gray-400">Fine-tune the panels. Automatic accent and background colors follow.</p>
        <UiButton variant="secondary" size="sm" type="button" :disabled="activeThemeHue === 0" @click="setThemeHue(0, account)">Reset hue</UiButton>
      </div>
    </div>

    <div class="space-y-2">
      <div class="flex items-baseline justify-between gap-3">
        <label class="text-xs font-bold uppercase text-gray-500" for="main-brightness">Main color brightness</label>
        <output class="text-sm text-gray-700" for="main-brightness">{{ mainBrightnessLabel }}</output>
      </div>
      <input id="main-brightness" :value="activeAppearance.mainBrightness" class="appearance-wash-slider w-full" type="range" :min="mainRange.min" :max="mainRange.max" step="1" :aria-valuetext="mainBrightnessLabel" aria-describedby="main-brightness-help" @input="updateAppearance({ mainBrightness: numberValue($event) }, account)" />
      <div class="flex justify-between text-xs text-gray-500"><span>Darker</span><span>Lighter</span></div>
      <p id="main-brightness-help" class="text-xs text-gray-400">Adjust panels, cards, and other neutral elements. Accent text and focus indicators adapt for readability.</p>
    </div>

    <ul class="appearance-palette" aria-label="Theme palette">
      <li v-for="swatch in swatches" :key="swatch.label">
        <span class="appearance-palette__swatch" :style="{ backgroundColor: `var(${swatch.token})` }" aria-hidden="true"></span>
        <span>{{ swatch.label }}</span>
      </li>
    </ul>

    <details class="appearance-settings">
      <summary class="cursor-pointer rounded text-sm font-medium text-gray-800">Color and background settings</summary>
      <div class="space-y-5 pt-4">
        <label class="appearance-toggle">
          <input type="checkbox" :checked="activeAppearance.showArtwork" @change="updateAppearance({ showArtwork: checkedValue($event) }, account)" />
          <span>Show background artwork</span>
        </label>
        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="background-artwork-preference">Background artwork</label>
          <select id="background-artwork-preference" v-model="selectedArtwork" :disabled="!activeAppearance.showArtwork" class="ui-input w-full">
            <option v-for="artwork in backgroundArtworkOptions" :key="artwork.value" :value="artwork.value">{{ artwork.label }}</option>
          </select>
        </div>

        <fieldset v-for="role in colorRoles" :key="role.key" class="space-y-2">
          <legend class="mb-2 text-xs font-bold uppercase text-gray-500">{{ role.label }}</legend>
          <label class="appearance-toggle">
            <input type="checkbox" :checked="activeAppearance[role.key].automatic" @change="setAutomaticColor(role.key, checkedValue($event), account)" />
            <span>{{ role.automaticLabel }}</span>
          </label>
          <div v-if="!activeAppearance[role.key].automatic" class="space-y-3">
            <div v-for="control in colorSliders" :key="control.key">
              <div class="flex items-baseline justify-between gap-3">
                <label :for="`${role.key}-${control.key}`" class="text-sm text-gray-700">{{ role.label }} {{ control.label }}</label>
                <output :for="`${role.key}-${control.key}`" class="text-sm text-gray-700">{{ Math.round(activeAppearanceColors[role.key][control.key]) }}{{ control.suffix }}</output>
              </div>
              <input :id="`${role.key}-${control.key}`" class="appearance-wash-slider w-full" type="range" min="0" :max="control.max" step="1" :value="activeAppearanceColors[role.key][control.key]" :aria-valuetext="`${Math.round(activeAppearanceColors[role.key][control.key])}${control.suffix}`" @input="setManualColor(role.key, { [control.key]: numberValue($event) }, account)" />
            </div>
          </div>
        </fieldset>

        <fieldset :disabled="!activeAppearance.showArtwork" class="space-y-3 disabled:opacity-60">
          <legend class="mb-2 text-xs font-bold uppercase text-gray-500">Artwork brightness</legend>
          <div v-for="control in brightnessSliders" :key="control.key">
            <div class="flex items-baseline justify-between gap-3">
              <label :for="control.key" class="text-sm text-gray-700">{{ control.label }}</label>
              <output :for="control.key" class="text-sm text-gray-700">{{ activeAppearance[control.key] }}%</output>
            </div>
            <input :id="control.key" class="appearance-wash-slider w-full" type="range" min="0" max="100" step="1" :value="activeAppearance[control.key]" :aria-valuetext="`${activeAppearance[control.key]}%`" aria-describedby="artwork-brightness-help" @input="updateAppearance({ [control.key]: numberValue($event) }, account)" />
          </div>
          <p id="artwork-brightness-help" class="text-xs text-gray-400">Min and Max set perceived brightness before wash. Wash is applied last, mixing in {{ isDarkTheme(activeTheme) ? 'black' : 'white' }}. Equal Min and Max produce a flat background.</p>
        </fieldset>

        <figure class="space-y-2">
          <figcaption class="text-xs text-gray-500">Background tones after wash</figcaption>
          <div role="img" aria-label="Background tones after wash" class="appearance-ramp" :style="{ backgroundImage: rampGradient }"></div>
          <p v-if="!activeAppearance.showArtwork" class="text-xs text-gray-400">Artwork is hidden. The app uses the solid Background swatch.</p>
        </figure>
        <UiButton variant="secondary" size="sm" type="button" :disabled="isDefault" @click="resetThemeAppearance(account)">Reset this theme</UiButton>
      </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { UiButton } from '../ui';
import { defaultAppearance, isDarkTheme, isNamedTheme, mainBrightnessRange, type AppTheme, type BackgroundArtwork } from '@pror0/appearance';
import { useAuth } from '../composables/useAuth';
import { activeAppearance, activeAppearanceColors, activeArtworkRamp, activeTheme, activeThemeHue, backgroundArtworkOptions,
  resetThemeAppearance, setAutomaticColor, setManualColor, setTheme, setThemeHue, themeOptions, updateAppearance } from '../composables/useTheme';

const { currentUser } = useAuth();
const account = computed(() => currentUser.value?.user_id);
const selectedTheme = computed({ get: () => activeTheme.value, set: (theme: AppTheme) => setTheme(theme, account.value) });
const selectedArtwork = computed({ get: () => activeAppearance.value.artwork, set: (artwork: BackgroundArtwork) => updateAppearance({ artwork }, account.value) });
const themeGroups = ['Base', 'Light palettes', 'Dark palettes'];
const hueLabel = computed(() => `${activeThemeHue.value > 0 ? '+' : ''}${activeThemeHue.value}°`);
const mainRange = computed(() => mainBrightnessRange(activeTheme.value, activeAppearance.value));
const mainBrightnessLabel = computed(() => `${activeAppearance.value.mainBrightness > 0 ? '+' : ''}${activeAppearance.value.mainBrightness}`);
const swatches = [{ label: 'Main', token: '--ui-surface' }, { label: 'Accent', token: '--ui-primary' }, { label: 'Background', token: '--ui-canvas' }];
const colorRoles = [{ key: 'accent', label: 'Accent', automaticLabel: 'Automatic accent color' }, { key: 'background', label: 'Background', automaticLabel: 'Automatic background tint' }] as const;
const colorSliders = [{ key: 'hue', label: 'hue', suffix: '°', max: 359 }, { key: 'strength', label: 'color strength', suffix: '%', max: 100 }] as const;
const brightnessSliders = [{ key: 'minBrightness', label: 'Minimum brightness' }, { key: 'maxBrightness', label: 'Maximum brightness' }, { key: 'wash', label: 'Background wash' }] as const;
const numberValue = (event: Event) => Number((event.target as HTMLInputElement).value);
const checkedValue = (event: Event) => (event.target as HTMLInputElement).checked;
const isDefault = computed(() => JSON.stringify(activeAppearance.value) === JSON.stringify(defaultAppearance(activeTheme.value)));
const rampGradient = computed(() => `linear-gradient(to right, ${activeArtworkRamp.value.washed.map(rgb => `rgb(${rgb.join(' ')})`).join(', ')})`);
</script>
